<script setup lang="ts">
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import StepSelectFile from './StepSelectFile.vue'
import StepMapping from './StepMapping.vue'
import StepConfirm from './StepConfirm.vue'
import StepResult from './StepResult.vue'
import TrackRecordService from '@/services/mandate/trackRecordService.ts'
import { TrackRecordSourceEnum } from '@/enums/trackRecord_enum.ts'
import type {
  TrackRecordAnalyzeResponse,
  TrackRecordWizardState,
} from '@/types/services/mandate/trackRecord.type.ts'

const props = defineProps<{
  mandateId: number
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { t } = useI18n()

const state = reactive<TrackRecordWizardState>({
  step: 1,
  mandateId: props.mandateId,
  file: null,
  source: TrackRecordSourceEnum.BACKTESTING,
  analysis: null,
  mapping: {
    date_column: null,
    mode: null,
    value_column: null,
    perf_column: null,
  },
  perf_type: null,
  perf_unit: null,
  capital_initial: 100,
  replace_existing: false,
  result: null,
  loadingAnalyze: false,
  loadingImport: false,
  error: null,
})

const STEP_LABELS = [
  t('views.trackRecord.steps.selectFile'),
  t('views.trackRecord.steps.mapping'),
  t('views.trackRecord.steps.confirm'),
  t('views.trackRecord.steps.result'),
]

function goNext() {
  state.step = Math.min(state.step + 1, 4)
}

function goBack() {
  state.step = Math.max(state.step - 1, 1)
}

function onAnalyzed(analysis: TrackRecordAnalyzeResponse) {
  state.analysis = analysis
}

async function runImport() {
  if (!state.file || !state.mapping.date_column || !state.mapping.mode) return

  state.loadingImport = true
  state.error = null

  try {
    state.result = await TrackRecordService.import(props.mandateId, {
      file: state.file,
      date_column: state.mapping.date_column,
      source: state.source,
      mode: state.mapping.mode,
      value_column: state.mapping.value_column ?? undefined,
      perf_column: state.mapping.perf_column ?? undefined,
      perf_type: state.perf_type ?? undefined,
      perf_unit: state.perf_unit,
      capital_initial: state.capital_initial,
      replace_existing: state.replace_existing,
    })
    state.step = 4
  } catch (err: unknown) {
    // Try to extract backend error key from Axios error
    const anyErr = err as { response?: { data?: { error_key?: string; detail?: string } } }
    const errorKey =
      anyErr?.response?.data?.error_key ??
      anyErr?.response?.data?.detail ??
      null
    state.error = errorKey
    state.step = 4
  } finally {
    state.loadingImport = false
  }
}

function onCorrect() {
  // Go back to mapping step, keeping file and analysis
  state.step = 2
  state.result = null
  state.error = null
}

function onNewImport() {
  // Full reset
  Object.assign(state, {
    step: 1,
    file: null,
    source: TrackRecordSourceEnum.BACKTESTING,
    analysis: null,
    mapping: { date_column: null, mode: null, value_column: null, perf_column: null },
    perf_type: null,
    perf_unit: null,
    capital_initial: 100,
    replace_existing: false,
    result: null,
    loadingAnalyze: false,
    loadingImport: false,
    error: null,
  })
}
</script>

<template>
  <v-row>
    <!-- Stepper header -->
    <v-col cols="12">
      <v-stepper
        :model-value="state.step"
        alt-labels
        flat
        non-linear
        class="bg-transparent"
      >
        <v-stepper-header>
          <template v-for="(label, i) in STEP_LABELS" :key="i">
            <v-stepper-item
              :value="i + 1"
              :title="label"
              :complete="state.step > i + 1"
              :color="state.step > i + 1 ? 'primary' : undefined"
            />
            <v-divider v-if="i < STEP_LABELS.length - 1" />
          </template>
        </v-stepper-header>
      </v-stepper>
    </v-col>

    <!-- Step content -->
    <v-col cols="12">
      <!-- Step 1: Select file -->
      <StepSelectFile
        v-if="state.step === 1"
        :file="state.file"
        @update:file="state.file = $event"
        @next="goNext"
      />

      <!-- Step 2: Mapping -->
      <StepMapping
        v-else-if="state.step === 2 && state.file"
        :mandate-id="props.mandateId"
        :file="state.file"
        :mapping="state.mapping"
        :perf-type="state.perf_type"
        :perf-unit="state.perf_unit"
        :capital-initial="state.capital_initial"
        :replace-existing="state.replace_existing"
        :initial-analysis="state.analysis ?? undefined"
        @update:mapping="state.mapping = $event"
        @update:perf-type="state.perf_type = $event"
        @update:perf-unit="state.perf_unit = $event"
        @update:capital-initial="state.capital_initial = $event"
        @update:replace-existing="state.replace_existing = $event"
        @analyzed="onAnalyzed"
        @next="goNext"
        @back="goBack"
      />

      <!-- Step 3: Confirm -->
      <StepConfirm
        v-else-if="state.step === 3 && state.analysis"
        :mandate-id="props.mandateId"
        :source="state.source"
        :analysis="state.analysis"
        :mapping="state.mapping"
        :perf-type="state.perf_type"
        :perf-unit="state.perf_unit"
        :capital-initial="state.capital_initial"
        :replace-existing="state.replace_existing"
        :loading="state.loadingImport"
        @import="runImport"
        @back="goBack"
      />

      <!-- Step 4: Result -->
      <StepResult
        v-else-if="state.step === 4"
        :result="state.result"
        :error="state.error"
        @correct="onCorrect"
        @new-import="onNewImport"
      />
    </v-col>
  </v-row>
</template>
